import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useWorkflows } from '../hooks/useWorkflows';
import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
} from '../hooks/useSchedules';
import {
  useTriggers,
  useCreateTrigger,
  useUpdateTrigger,
  useRotateTrigger,
  useDeleteTrigger,
} from '../hooks/useTriggers';

const CRON_PRESETS = [
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every day at 09:00 UTC', value: '0 9 * * *' },
  { label: 'Every Monday at 09:00 UTC', value: '0 9 * * 1' },
  { label: 'Custom cron…', value: 'custom' },
];

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export default function Schedules() {
  const user = useAuthStore((s) => s.user);
  const { data: wfData } = useWorkflows({ limit: 100 });
  const { data: schedules, isLoading } = useSchedules();
  const update = useUpdateSchedule();
  const remove = useDeleteSchedule();

  const workflows = (wfData?.data ?? []).filter((w) => w.status === 'ACTIVE');
  const wfName = (id: string) =>
    (wfData?.data ?? []).find((w) => w.id === id)?.name ?? id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <span className="text-sm font-medium">Schedules</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2">Scheduled workflows</h2>
          <p className="text-muted-foreground">
            Run a workflow automatically on a schedule — no manual trigger needed.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-[1fr_1.3fr]">
          <CreateScheduleForm workflows={workflows} />

          <div className="space-y-3">
            {isLoading ? (
              <LoadingSkeleton className="h-32" />
            ) : (schedules?.length ?? 0) === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No schedules yet.
                </CardContent>
              </Card>
            ) : (
              schedules!.map((s) => (
                <Card key={s.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium">{wfName(s.workflowId)}</div>
                        <div className="text-xs text-muted-foreground">
                          <code>{s.cron}</code>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {s.enabled ? `Next: ${fmt(s.nextRunAt)}` : 'Paused'}
                          {s.lastRunAt ? ` · Last: ${fmt(s.lastRunAt)}` : ''}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={update.isPending}
                          onClick={() =>
                            update.mutate({
                              id: s.id,
                              data: { enabled: !s.enabled },
                            })
                          }
                        >
                          {s.enabled ? 'Pause' : 'Resume'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(s.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        <TriggersSection workflows={workflows} wfName={wfName} />
      </main>
    </div>
  );
}

function TriggersSection({
  workflows,
  wfName,
}: {
  workflows: { id: string; name: string }[];
  wfName: (id: string) => string;
}) {
  const { data: triggers } = useTriggers();
  const create = useCreateTrigger();
  const update = useUpdateTrigger();
  const rotate = useRotateTrigger();
  const remove = useDeleteTrigger();
  const [workflowId, setWorkflowId] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  function copy(url: string, id: string) {
    navigator.clipboard?.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500);
  }

  return (
    <div className="mt-10">
      <div className="mb-4">
        <h3 className="text-2xl font-bold">Webhook triggers</h3>
        <p className="text-muted-foreground">
          Run a workflow when an external system POSTs to a private URL.
        </p>
      </div>

      <Card className="mb-4">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 space-y-1.5 min-w-[200px]">
              <Label htmlFor="trig-wf">Workflow</Label>
              <select
                id="trig-wf"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select a workflow…</option>
                {workflows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              disabled={!workflowId || create.isPending}
              onClick={() =>
                create.mutate(workflowId, { onSuccess: () => setWorkflowId('') })
              }
            >
              Create trigger
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {(triggers ?? []).map((t) => (
          <Card key={t.id}>
            <CardContent className="py-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium">{wfName(t.workflowId)}</div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate({ id: t.id, enabled: !t.enabled })
                    }
                  >
                    {t.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rotate.isPending}
                    onClick={() => rotate.mutate(t.id)}
                  >
                    Rotate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(t.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Input readOnly value={t.hookUrl} className="font-mono text-xs" />
                <Button size="sm" variant="secondary" onClick={() => copy(t.hookUrl, t.id)}>
                  {copied === t.id ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                POST here to run the workflow (the JSON body becomes its input).{' '}
                {t.enabled ? '' : 'Paused. '}
                {t.lastTriggeredAt
                  ? `Last fired ${new Date(t.lastTriggeredAt).toLocaleString()}.`
                  : ''}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CreateScheduleForm({
  workflows,
}: {
  workflows: { id: string; name: string }[];
}) {
  const create = useCreateSchedule();
  const [workflowId, setWorkflowId] = useState('');
  const [preset, setPreset] = useState(CRON_PRESETS[2].value);
  const [customCron, setCustomCron] = useState('');
  const [inputJson, setInputJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!workflowId) {
      setError('Pick a workflow.');
      return;
    }
    const cron = preset === 'custom' ? customCron.trim() : preset;
    if (!cron) {
      setError('Enter a cron expression.');
      return;
    }
    let inputData: Record<string, unknown> | undefined;
    if (inputJson.trim()) {
      try {
        inputData = JSON.parse(inputJson);
      } catch {
        setError('Input must be valid JSON.');
        return;
      }
    }
    create.mutate(
      { workflowId, cron, inputData },
      {
        onSuccess: () => {
          setInputJson('');
        },
        onError: (err: any) =>
          setError(err.response?.data?.detail || 'Could not create schedule'),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">New schedule</CardTitle>
        <CardDescription>Only active workflows can be scheduled.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="wf">Workflow</Label>
            <select
              id="wf"
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Select a workflow…</option>
              {workflows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="preset">Runs</Label>
            <select
              id="preset"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {preset === 'custom' && (
            <div className="space-y-1.5">
              <Label htmlFor="cron">Cron expression (UTC)</Label>
              <Input
                id="cron"
                placeholder="0 9 * * 1-5"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="input">Input (optional JSON)</Label>
            <Textarea
              id="input"
              rows={3}
              placeholder='{ "message": "run the daily report" }'
              value={inputJson}
              onChange={(e) => setInputJson(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {workflows.length === 0 && (
            <p className="text-xs text-muted-foreground">
              You have no active workflows yet.
            </p>
          )}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create schedule'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
